// <pfs-stepper>: rein präsentativer Fortschritts-Header für mehrstufige
// Abläufe (PFS-559). Zeigt nummerierte Schritte + Verbinder + Labels und
// hebt den aktiven Schritt hervor — damit der User sieht, was bis zum Ziel
// noch ansteht (z.B. Login-MFA-Setup, später Terminbuchung).
//
// BEWUSST DUMM: hält KEINEN State und orchestriert NICHTS. Welcher Schritt
// dran ist, entscheidet der Host/BFF und füttert es per Attribut. Der
// generische, FE-getriebene <pfs-wizard>-Container (next/back, Inhalts-Swap)
// ist ein eigenes Primitiv und kommt mit der Terminbuchung.
//
// Attribute (alle data-*):
//   data-steps   required — Schritt-Labels, durch "|" getrennt
//                (z.B. "Passwort|MFA einrichten|Fertig"). "|" gewählt, weil
//                Labels eher Kommas als Pipes enthalten.
//   data-active  0-basierter Index des aktiven Schritts (default 0). Schritte
//                davor = erledigt, dieser = aktiv, danach = ausstehend. Index
//                >= Anzahl → alle erledigt (Abschluss-Anzeige).
//
// Brandbar: ausschließlich @pfs/design-Tokens (--pfs-*), keine Literale außer
// Token-Fallbacks; folgt damit dem Plattform-WC-Kanon (auch im Dark prüfen).

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { escapeHTML } from "../../utils/escape-html.js";

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; width: 100%; }

.steps {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0;
  padding: 0.25rem 0 0.5rem;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 0;
  min-width: 0;
  position: relative;
}

/* Verbinder-Linie zwischen den Punkten — als ::before des jeweils
   rechten Schritts gezeichnet, von links bis zur Punkt-Mitte. */
.step:not(:first-child)::before {
  content: "";
  position: absolute;
  top: 0.875rem;            /* Punkt-Mitte (Punkt 1.75rem hoch) */
  right: 50%;
  left: -50%;
  height: 2px;
  background: var(--pfs-color-outline);
}
.step.done::before,
.step.active::before {
  background: var(--pfs-color-primary);
}

.dot {
  position: relative;
  z-index: 1;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  box-sizing: border-box;
  background: var(--pfs-color-surface);
  border: 2px solid var(--pfs-color-outline);
  color: var(--pfs-color-on-surface-subtle);
}

.step.done .dot {
  background: var(--pfs-color-primary);
  border-color: var(--pfs-color-primary);
  color: var(--pfs-color-on-primary);
}
.step.active .dot {
  background: var(--pfs-color-primary);
  border-color: var(--pfs-color-primary);
  color: var(--pfs-color-on-primary);
  /* Halo-Ring aus der Brand-Primary ableiten (translucent), NICHT aus dem
     Info-Status-Token — sonst blauer Ring auf z.B. Ember-Brand. Folgt dem
     Dot/Connector (= primary) je Brand. */
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--pfs-color-primary) 20%, transparent);
}

.label {
  font-size: 0.8125rem;
  line-height: 1.2;
  text-align: center;
  padding: 0 0.25rem;
  color: var(--pfs-color-on-surface-subtle);
  overflow-wrap: anywhere;
}
.step.done .label { color: var(--pfs-color-on-surface-muted); }
.step.active .label { color: var(--pfs-color-on-surface); font-weight: 600; }
`;

const CHECK = `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

defineComponent({
  tagName: "pfs-stepper",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    const ol = document.createElement("ol");
    ol.className = "steps";
    ol.setAttribute("role", "list");
    root.append(style, ol);

    // data-steps/data-active reaktiv — der Host schiebt den aktiven Schritt vor,
    // ohne das Element neu zu erzeugen (uniform mit badge/avatar/card-header).
    const paint = (): void => {
      const labels = (host.dataset.steps ?? "")
        .split("|")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const active = parseInt(host.dataset.active ?? "0", 10) || 0;
      ol.innerHTML = labels
        .map((label, i) => {
          const status = i < active ? "done" : i === active ? "active" : "upcoming";
          const inner = status === "done" ? CHECK : String(i + 1);
          const aria = status === "active" ? ' aria-current="step"' : "";
          return `<li class="step ${status}"${aria}><span class="dot" aria-hidden="true">${inner}</span><span class="label">${escapeHTML(label)}</span></li>`;
        })
        .join("");
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, { attributes: true, attributeFilter: ["data-steps", "data-active"] });
  },
});

